var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SdkApi } from '@or-sdk/sdk-api';
import axios from 'axios';
import { isNode } from 'browser-or-node';
import FormDataLib from 'form-data';
import Cookie from 'universal-cookie';
import { generateFingerPrint, NoRightsError } from './utils';
const defaultCookiePollingInterval = 500;
const defaultExpireInShort = 3600000;
const defaultExpireInLong = 30758400000;
export class Auth {
    constructor(params) {
        this.multiUserRefreshRetryAt = 0;
        this.loggedIn = false;
        const { discoveryUrl, sdkUrl, cookieDomain, allowGuestLogin, userCookieName, userExpireCookieName, cookiePrefix, multiUserCookieName, cookiePollingInterval, expireInShort, expireInLong, allowIframe, authUiUrl, multiUserToken, } = params;
        this.discoveryUrl = discoveryUrl;
        this.sdkUrl = sdkUrl;
        this.authUiUrl = authUiUrl;
        this.cookie = new Cookie();
        this.cookieDomain = cookieDomain;
        this.allowGuestLogin = Boolean(allowGuestLogin);
        this.allowIframe = allowIframe;
        this.deprecatedCookieName = userCookieName || 'user';
        this.deprecatedUserExpireCookieName = userExpireCookieName || 'user_expire';
        this.deprecatedDomain = { domain: '.onereach.ai' };
        this.userCookieName = cookiePrefix ? cookiePrefix : `${userCookieName}_new`;
        this.multiUserCookieName = multiUserCookieName;
        this.userExpireCookieName = `${this.userCookieName}_expire`;
        this.cookiePollingInterval = cookiePollingInterval || defaultCookiePollingInterval;
        this.expireInShort = expireInShort ? +expireInShort : defaultExpireInShort;
        this.expireInLong = expireInLong ? +expireInLong : defaultExpireInLong;
        this._multiUserToken = multiUserToken;
    }
    get domain() {
        return this.cookieDomain ? { domain: this.cookieDomain } : {};
    }
    get _getUser() {
        return this.cookie.get(this.userCookieName) || null;
    }
    startResetPassword(email, redirectPath) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.createSdkApi().makeRequest({
                route: '/password/forgot-start',
                method: 'POST',
                data: {
                    email,
                    redirectPath,
                },
            });
        });
    }
    endResetPassword(_a) {
        return __awaiter(this, arguments, void 0, function* ({ email, key, password, type }) {
            return this.createSdkApi().makeRequest({
                route: '/password/forgot-end',
                method: 'POST',
                data: {
                    email,
                    key,
                    password,
                    type,
                },
            });
        });
    }
    createMultiUser(_a) {
        return __awaiter(this, arguments, void 0, function* ({ token, type, password, profile }) {
            return this.createSdkApi().makeRequest({
                route: '/multi-user/create-finish',
                method: 'POST',
                data: {
                    token,
                    password,
                    type,
                    profile,
                },
            });
        });
    }
    createSdkApi(token = '') {
        return new SdkApi({
            token,
            discoveryUrl: this.discoveryUrl,
            sdkUrl: this.sdkUrl,
        });
    }
    getUserExpire(cookieName) {
        const expireCookie = this.cookie.get(cookieName || this.userExpireCookieName);
        return expireCookie ? parseInt(expireCookie, 10) : this.expireInShort;
    }
    _getNextExpiration(expireIn) {
        return new Date(Date.now() + expireIn);
    }
    stopCookieMonitor() {
        if (window) {
            clearTimeout(Number(this.monitorCookieTimeout));
        }
    }
    monitorCookie(logoutCallback, loginCallback) {
        this.stopCookieMonitor();
        this.monitorCookieTimeout = setTimeout(() => {
            const user = this._getUser;
            if (user) {
                if (!this.loggedIn) {
                    this.loggedIn = true;
                }
                if (typeof loginCallback === 'function') {
                    loginCallback(user);
                }
            }
            else if (this.loggedIn) {
                this.loggedIn = false;
                if (typeof logoutCallback === 'function') {
                    logoutCallback();
                }
            }
            this.monitorCookie(logoutCallback, loginCallback);
        }, this.cookiePollingInterval);
    }
    _saveCookies(user, expireIn) {
        const nextExpiration = this._getNextExpiration(expireIn);
        this.cookie.set(this.userCookieName, user, Object.assign({ path: '/', expires: nextExpiration, secure: true, sameSite: this.allowIframe ? 'none' : 'lax' }, this.domain));
        this.cookie.set(this.userExpireCookieName, expireIn, Object.assign({ path: '/', expires: nextExpiration, secure: true, sameSite: this.allowIframe ? 'none' : 'lax' }, this.domain));
    }
    saveMultiUser(user, expireLong) {
        return __awaiter(this, void 0, void 0, function* () {
            const sdkApi = this.createSdkApi((user === null || user === void 0 ? void 0 : user.token) || '');
            const data = yield sdkApi.makeRequest({
                method: 'get',
                withCredentials: true,
                params: {
                    expireLong,
                },
                route: '/multi-user/token',
            });
            this._multiUser = data;
            return data;
        });
    }
    updateCookieExpiration() {
        const user = this._getUser;
        if (!user)
            return;
        if (!user.multiUserId) {
            this._saveCookies(user, this.getUserExpire());
            return;
        }
        if (this.refreshSessionPromise)
            return;
        const now = Date.now();
        const userExpiring = user.expire < now + 1000 * 60;
        const multiUserExpiring = !this._multiUser || this._multiUser.expire < now + 1000 * 60;
        if (!userExpiring && !multiUserExpiring)
            return;
        if (now < this.multiUserRefreshRetryAt && user.expire > now)
            return;
        this.refreshSessionPromise = this.refreshProfileSession(user, userExpiring)
            .finally(() => {
            this.refreshSessionPromise = undefined;
        });
    }
    refreshProfileSession(user, refreshUser) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            let multiUserRefreshed = true;
            try {
                yield this.refreshMultiUserToken();
                this.multiUserRefreshRetryAt = 0;
            }
            catch (_b) {
                multiUserRefreshed = false;
                this.multiUserRefreshRetryAt = Date.now() + 1000 * 60;
            }
            if (!refreshUser)
                return;
            const currentUser = this._getUser;
            if (!currentUser || currentUser.token !== user.token)
                return;
            if (!multiUserRefreshed && currentUser.expire > Date.now())
                return;
            try {
                yield this.refreshUserToken();
                if (!multiUserRefreshed)
                    this.multiUserRefreshRetryAt = 0;
            }
            catch (_c) {
                if (((_a = this._getUser) === null || _a === void 0 ? void 0 : _a.token) === user.token)
                    this.removeCookies();
                this._multiUser = undefined;
            }
        });
    }
    _validateGuest(role, allowGuestLoginOverride = false) {
        if (!(this.allowGuestLogin || allowGuestLoginOverride) && (role === null || role === void 0 ? void 0 : role.toLowerCase()) === 'guest') {
            throw new NoRightsError('Authorization for guests is not allowed');
        }
    }
    saveUser(_a) {
        return __awaiter(this, arguments, void 0, function* ({ long, user }) {
            this._saveCookies(user, long ? this.expireInLong : this.expireInShort);
            if (typeof this.tokenUpdateCallback === 'function')
                this.tokenUpdateCallback(user);
        });
    }
    signIn(_a) {
        return __awaiter(this, arguments, void 0, function* ({ credentials, long, allowGuestLogin = false, captchaToken, isCaptchaV2Checkbox, verificationCode, userToken, rememberTwoFactor, googleCode, }) {
            const fingerPrint = yield generateFingerPrint();
            const options = Object.assign(Object.assign(Object.assign(Object.assign({}, credentials), { captchaToken,
                isCaptchaV2Checkbox,
                verificationCode,
                fingerPrint,
                rememberTwoFactor, expireLong: long }), userToken ? { userToken } : {}), { googleCode });
            const sdkApi = this.createSdkApi();
            const user = yield sdkApi.makeRequest({
                method: 'POST',
                route: '/auth/token',
                data: options,
                withCredentials: true,
            });
            this._validateGuest(user.role, allowGuestLogin);
            if (!user.twoFactorCheck && !user.captchaCheck) {
                if (user.tokenType === 'multi-user') {
                    this._multiUser = user;
                }
                else {
                    yield this.saveUser({
                        long,
                        user,
                    });
                }
            }
            return user;
        });
    }
    _checkDeprecatedCookies() {
        const user = this.cookie.get(this.deprecatedCookieName);
        if (user) {
            const deprecatedUserExpireValue = this.getUserExpire(this.deprecatedUserExpireCookieName);
            const nextExpiration = this._getNextExpiration(deprecatedUserExpireValue);
            this.cookie.set(this.userCookieName, user, Object.assign({ path: '/', expires: nextExpiration, secure: true }, this.domain));
            this.cookie.set(this.userExpireCookieName, deprecatedUserExpireValue, Object.assign({ path: '/', expires: nextExpiration, secure: true }, this.domain));
        }
    }
    _validateToken(token_1) {
        return __awaiter(this, arguments, void 0, function* (token, allowGuestLogin = false, suspended) {
            const sdkApi = this.createSdkApi(token);
            const user = yield sdkApi.makeRequest({
                method: 'GET',
                route: suspended ? 'payments/auth/token' : '/auth/token',
            });
            this._validateGuest(user.role, allowGuestLogin);
            return user;
        });
    }
    signInWithToken(token_1) {
        return __awaiter(this, arguments, void 0, function* (token, allowGuestLogin = false, shouldNotSaveCookies = false) {
            var _a, _b, _c, _d;
            let userInfo = {};
            if (token != null && typeof token === 'object') {
                allowGuestLogin = (_a = token.allowGuestLogin) !== null && _a !== void 0 ? _a : false;
                shouldNotSaveCookies = (_b = token.shouldNotSaveCookies) !== null && _b !== void 0 ? _b : false;
                userInfo = (_c = token.userInfo) !== null && _c !== void 0 ? _c : {};
                token = token.token;
            }
            if (!token) {
                return Promise.reject(new Error('No token given'));
            }
            this._checkDeprecatedCookies();
            const user = yield this._validateToken(token, allowGuestLogin);
            Object.assign(user, { token });
            const allow = (_d = user.allow) !== null && _d !== void 0 ? _d : false;
            if (!allow) {
                throw Error('Invalid Token');
            }
            if (!shouldNotSaveCookies) {
                this._saveCookies(Object.assign(Object.assign({}, userInfo), user), this.getUserExpire());
            }
            return user;
        });
    }
    removeCookies() {
        this.cookie.remove(this.userExpireCookieName, Object.assign({ path: '/' }, this.domain));
        this.cookie.remove(this.userCookieName, Object.assign({ path: '/' }, this.domain));
        this.cookie.remove(this.multiUserCookieName, Object.assign({ path: '/' }, this.domain));
        this.cookie.remove(this.deprecatedCookieName, Object.assign({ path: '/' }, this.domain));
        this.cookie.remove(this.deprecatedUserExpireCookieName, Object.assign({ path: '/' }, this.domain));
        this.cookie.remove(this.deprecatedCookieName, Object.assign({ path: '/' }, this.domain));
        this.cookie.remove(this.deprecatedUserExpireCookieName, Object.assign({ path: '/' }, this.domain));
    }
    signOut(token) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const promises = [];
                if (token) {
                    const sdkApi = this.createSdkApi(token);
                    promises.push(sdkApi.makeRequest({
                        method: 'DELETE',
                        route: '/auth/fingerprint-token',
                    }));
                }
                const sdkApi = this.createSdkApi();
                promises.push(sdkApi.makeRequest({
                    method: 'DELETE',
                    route: '/multi-user/fingerprint-token',
                    withCredentials: true,
                }));
                yield Promise.all(promises);
            }
            catch (error) {
                console.log('Logout failed: ', error);
            }
            this.removeCookies();
        });
    }
    validateUser() {
        return __awaiter(this, arguments, void 0, function* (allowGuestLogin = false, shouldNotSaveCookies = false, suspended) {
            var _a;
            const userParams = this._getUser;
            if (!userParams) {
                return Promise.reject(new Error('No cached user params are available'));
            }
            const user = userParams.expire < Date.now()
                ? yield this.refreshUserToken()
                : yield this._validateToken(userParams.token, allowGuestLogin, suspended);
            if (!user.identityProvider && userParams.identityProvider) {
                user.identityProvider = userParams.identityProvider;
            }
            const allow = (_a = user.allow) !== null && _a !== void 0 ? _a : false;
            if (!allow) {
                throw Error('Invalid Token');
            }
            if (shouldNotSaveCookies)
                return user;
            const _user = Object.assign(Object.assign({}, userParams), { role: user.role, twoFactorenabled: user.twoFactorEnabled });
            this._saveCookies(_user, this.getUserExpire());
            return user;
        });
    }
    hasUserParams() {
        return Boolean(this._getUser);
    }
    multiUserGetUsersList() {
        return __awaiter(this, void 0, void 0, function* () {
            const sdkApi = this.createSdkApi(this.multiUserToken);
            return sdkApi.makeRequest({
                withCredentials: true,
                method: 'GET',
                route: '/multi-user/list-users',
            });
        });
    }
    multiUserLoginUser(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountId, id, long }) {
            const sdkApi = this.createSdkApi(this.multiUserToken);
            const user = yield sdkApi.makeRequest({
                withCredentials: true,
                method: 'POST',
                route: '/multi-user/user-token',
                data: {
                    accountId,
                    id,
                },
            });
            yield this.saveUser({
                long,
                user,
            });
            return user;
        });
    }
    getUserNameForLockScreen(user) {
        return __awaiter(this, void 0, void 0, function* () {
            const multiUserCookie = this.cookie.get(this.multiUserCookieName) || {};
            if (multiUserCookie.username)
                return multiUserCookie.username;
            const { multiUserId, username, token } = user;
            if (multiUserId) {
                const sdkApi = this.createSdkApi(token);
                const { username } = yield sdkApi.makeRequest({
                    method: 'GET',
                    route: '/user/multi-user',
                });
                return username;
            }
            return username;
        });
    }
    multiUserGetProfile() {
        return __awaiter(this, void 0, void 0, function* () {
            const sdkApi = this.createSdkApi(this.multiUserToken);
            return sdkApi.makeRequest({
                withCredentials: true,
                method: 'GET',
                route: '/multi-user/profile',
            });
        });
    }
    multiUserSetProfile(profile) {
        return __awaiter(this, void 0, void 0, function* () {
            const sdkApi = this.createSdkApi(this.multiUserToken);
            return sdkApi.makeRequest({
                withCredentials: true,
                method: 'POST',
                route: '/multi-user/profile',
                data: profile,
            });
        });
    }
    validateMultiUserToken(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const sdkApi = this.createSdkApi(token || this.multiUserToken);
            const data = yield sdkApi.makeRequest({
                withCredentials: true,
                method: 'GET',
                route: '/multi-user/token',
            });
            this._multiUser = data;
            return data;
        });
    }
    multiUserUploadIcon(_a) {
        return __awaiter(this, arguments, void 0, function* ({ name, contentType, cacheControl = 'no-cache', file, }) {
            const sdkApi = this.createSdkApi(this.multiUserToken);
            const data = yield sdkApi.makeRequest({
                withCredentials: true,
                method: 'POST',
                route: '/multi-user/sign-upload-url',
                data: {
                    name,
                    contentType,
                    cacheControl,
                },
            });
            if (isNode) {
                const formData = new FormDataLib();
                Object.entries(data.uploadParams.fields).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append('cache-control', cacheControl);
                formData.append('content-type', contentType);
                formData.append('File', file, name);
                yield new Promise((resolve, reject) => {
                    formData.submit(data.uploadParams.url, (error) => {
                        if (error)
                            return reject(error);
                        resolve();
                    });
                });
            }
            else {
                const formData = new FormData();
                Object.entries(data.uploadParams.fields).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append('cache-control', cacheControl);
                formData.append('content-type', contentType);
                formData.append('File', file, name);
                yield axios.post(data.uploadParams.url, formData);
            }
            return data.downloadUrl;
        });
    }
    confirmEmailChange(_a) {
        return __awaiter(this, arguments, void 0, function* ({ token }) {
            const sdkApi = this.createSdkApi();
            return sdkApi.makeRequest({
                method: 'POST',
                route: '/multi-user/change-email-finish',
                data: {
                    token,
                },
            });
        });
    }
    multiUserUpdateTwoFactor(_a) {
        return __awaiter(this, arguments, void 0, function* ({ secret, enabled, codes, verificationCode }) {
            const sdkApi = this.createSdkApi(this.multiUserToken);
            return sdkApi.makeRequest({
                method: 'PUT',
                route: '/multi-user/twofactor',
                withCredentials: true,
                data: {
                    secret,
                    enabled,
                    codes,
                    verificationCode,
                },
            });
        });
    }
    refreshUserToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const user = this._getUser;
                if (!user)
                    throw new Error('No user token given');
                return yield this.multiUserLoginUser({
                    accountId: user.accountId,
                    id: user.userId,
                    long: this.getUserExpire() === this.expireInLong,
                });
            }
            catch (error) {
                if (error instanceof Error && /No user token given/.test(error.message))
                    this.removeCookies();
                throw error;
            }
        });
    }
    refreshMultiUserToken() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.saveMultiUser();
        });
    }
    get multiUserToken() {
        if (typeof this._multiUserToken === 'string') {
            return this._multiUserToken;
        }
        if (typeof this._multiUserToken === 'function') {
            return this._multiUserToken();
        }
        return '';
    }
    getUserToken() {
        var _a;
        return (_a = this._getUser) === null || _a === void 0 ? void 0 : _a.token;
    }
}
//# sourceMappingURL=Auth.js.map